<!--
  Generic overlay component that handles opening state through the dedicated store
  Can be used with a drawerContent element inside for the common drawer layout
  <Overlay
    overlay-name="..."  #name of the overlay
    location="..."      # location of the overlay
    size="..."          # size name for overlay width
    order="..."         # order of the overlay if several ones can be over each other

    Slots:
    - content           # Main content of the overlay - exposes a close function that allow children to close the overlay + a params variable that holds any variable sent with the opening

    Exposed functions:
    - closeOverlay      # Method to close the overlay

    Provides:
    - scrollTop         # method that allows to go back to the top of the side panel
  />
-->
<script setup lang="ts">
/* UTILS */
import { computed, onUnmounted, provide, ref, watch } from 'vue';

/* STORE */
import { useOverlayStore } from '@/components/drawers/overlayStore';
/* COMPONENTS */
import CloseWarningModal from '@/components/modal/CloseWarningModal.vue';
import { useLocaleStore } from '@/stores/locales';
/* TYPES */
import type { OverlayName } from '@/types/overlays/overlays.types';

/* CONFIG */
import { sizeClasses, type OverlaySize } from './overlay.config';

/* PROPS */
type Props = {
  overlayName: OverlayName;
  location?: 'right' | 'left';
  size?: OverlaySize;
  order?: string;
  absolute?: boolean;
  fullHeight?: boolean;
  showWarningModal?: boolean;
  scrim?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  size: 'm',
  order: '1',
  absolute: false,
  fullHeight: true,
  showWarningModal: false,
  scrim: true
});

const emits = defineEmits(['close']);

/* STORES */
const overlayStore = useOverlayStore();
const localeStore = useLocaleStore();

/* OPEN STATE */
const isOverlayOpen = computed(() => overlayStore.getOverlayState(props.overlayName));
const isClosable = computed(() => overlayStore.isClosable(props.overlayName));

const isDrawerOpen = computed({
  get: () => overlayStore.overlays[props.overlayName]?.open || false,
  set: (value: boolean) => {
    if (props.showWarningModal) {
      isCloseModalOpen.value = true;
    } else if (isClosable.value !== false) {
      const overlay = overlayStore.overlays[props.overlayName];
      if (overlay) {
        overlay.open = value;
      }
    }
  }
});

/**
 * Handle overlay location based on props if registered or app direction (rtl)
 */
const getOverlayLocation = computed(() => props.location || (localeStore.getIsLocaleRtl ? 'left' : 'right'));

// REFS
const content = ref<HTMLElement>();
const isCloseModalOpen = ref(false);

/**
 * Allows to close the overlay
 */
function closeOverlay() {
  if (props.showWarningModal) {
    isCloseModalOpen.value = true;
  } else if (isClosable.value !== false) {
    overlayStore.toggle(props.overlayName);
  }
}

/**
 * Allows to scroll to the top of the content
 */
function scrollTop() {
  content.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/**
 * Force close drawer from the warning modal's submit
 */
function forceCloseDrawer() {
  isCloseModalOpen.value = false;
  const overlay = overlayStore.overlays[props.overlayName];
  if (overlay) {
    overlay.open = false;
  }
}

/** WATCHERS */
// When a side panel opens or closes, update overflow
watch(
  () => isOverlayOpen,
  () => {
    overlayStore.setHTMLOverflow();

    // When closing, reset params
    if (!isOverlayOpen.value) {
      overlayStore.resetParams(props.overlayName);
      emits('close');
    }
  },
  {
    deep: true,
    immediate: true
  }
);

/**
 * PROVIDES
 * Navigation drawer can provide helper functions to their child element
 */
provide('scrollTop', scrollTop); // If injected, this helper function allows to scroll to the top of the side panel content - note : if ever need to scroll to the top of the side panel (header included), just move the prepend slot into the main content element - does not change anything

/* EXPOSED */
defineExpose({
  closeOverlay // Expose method to close overlay
});

// Close Drawers on unmount because when we comeback to table like with browser "previous" button or else, the drawers was still open and blank
onUnmounted(() => {
  overlayStore.close(props.overlayName);
  overlayStore.setHTMLOverflow();
});
</script>

<template>
  <CloseWarningModal v-model="isCloseModalOpen" @click:submit="forceCloseDrawer" />

  <!-- Dark layover -->
  <transition name="fade" appear>
    <div
      v-if="isDrawerOpen && props.scrim"
      class="fixed inset-0 bg-black/40"
      :style="{ zIndex: Number(props.order) + 1000 }"
      @click="isClosable ? (isDrawerOpen = false) : null"
    />
  </transition>

  <!-- Panel -->
  <div
    class="top-0 bottom-0 h-full flex flex-col shadow-(--shadow-sidepanel) bg-white transition-transform duration-300 will-change-transform"
    :class="[
      sizeClasses[props.size],
      props.absolute ? 'absolute' : 'fixed',
      getOverlayLocation === 'right' ? 'right-0 rounded-l-xl' : 'left-0  rounded-r-xl ',
      isDrawerOpen ? 'translate-x-0' : getOverlayLocation === 'right' ? 'translate-x-full' : '-translate-x-full'
    ]"
    :style="{ zIndex: Number(props.order) + 1001 }"
  >
    <!-- Prepend/header slot -->
    <template v-if="overlayStore.getOverlayState(props.overlayName)">
      <slot name="header" :close="closeOverlay" />
      <div v-if="$slots.header" class="border-t" />
    </template>

    <!-- Content -->
    <div ref="content" :class="['flex flex-col overflow-y-auto', props.fullHeight ? 'h-full' : 'min-h-full']">
      <slot
        v-if="overlayStore.getOverlayState(props.overlayName)"
        name="content"
        :close="closeOverlay"
        :params="overlayStore.overlays[props.overlayName]?.params as any"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
